import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Map as MapIcon } from '@mui/icons-material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  ComposedChart,
  Line,
  LineChart,
  Legend,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
} from 'recharts';
import { AdminService } from '../../services/adminService';

/**
 * Paliers d'aventure : 6ᵉ, 12ᵉ et 16ᵉ niveau JOUABLE terminé (prologue et
 * épilogue exclus ; sur la Forêt le 16ᵉ est le boss). Combien de joueurs, en
 * combien de temps ; puis l'entonnoir niveau par niveau, pour voir où l'aventure
 * décroche. Définitions : `admin.queries` → `AVENTURE_PALIERS_CTE`,
 * `getAventureNiveaux`.
 */

const COULEURS = { 6: '#2196f3', 12: '#4caf50', 16: '#ff9800' };

const virgule = (v) => String(v).replace('.', ',');

/** Délai calendaire en jours → texte lisible (heures sous un jour). */
function formatDelai(jours) {
  if (jours === null || jours === undefined) return '—';
  if (jours < 1) return `${Math.round(jours * 24)} h`;
  return `${virgule(Math.round(jours * 10) / 10)} j`;
}

/** Minutes de jeu → « 42 min » ou « 2 h 05 ». */
function formatDuree(minutes) {
  if (minutes === null || minutes === undefined) return '—';
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const h = Math.floor(minutes / 60);
  return `${h} h ${String(Math.round(minutes - h * 60)).padStart(2, '0')}`;
}

function PalierCard({ p }) {
  return (
    <Card sx={{ height: '100%', borderTop: `4px solid ${COULEURS[p.palier] ?? '#607d8b'}` }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">
          Niveau {p.palier} atteint
        </Typography>
        <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
          {p.atteints}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          joueurs · {p.partComptesPct === null ? '—' : `${virgule(p.partComptesPct)} %`} des comptes ·{' '}
          {p.partAventuriersPct === null ? '—' : `${virgule(p.partAventuriersPct)} %`} de ceux qui ont fini un niveau
        </Typography>
        <Typography variant="body2">
          Délai depuis l'inscription : <strong>{formatDelai(p.joursMediane)}</strong>
          <Typography component="span" variant="caption" color="text.secondary">
            {' '}(médiane ; {formatDelai(p.joursP25)} – {formatDelai(p.joursP75)})
          </Typography>
        </Typography>
        <Typography variant="body2">
          Temps de jeu : <strong>{formatDuree(p.minutesMediane)}</strong>
          <Typography component="span" variant="caption" color="text.secondary">
            {' '}(médiane ; {formatDuree(p.minutesP25)} – {formatDuree(p.minutesP75)})
          </Typography>
        </Typography>
        <Typography variant="body2">
          Jours joués : <strong>{p.joursJouesMediane === null ? '—' : virgule(p.joursJouesMediane)}</strong>
          <Typography component="span" variant="caption" color="text.secondary"> (médiane)</Typography>
        </Typography>
      </CardContent>
    </Card>
  );
}

function InfoNiveau({ active, payload }) {
  if (!active || !payload?.length) return null;
  const n = payload[0].payload;
  const passage = n.debloques > 0 ? Math.round((n.termines / n.debloques) * 100) : null;
  return (
    <Box sx={{ bgcolor: 'background.paper', p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
        {n.ordre}. {n.nom} <Typography component="span" variant="caption">({n.type})</Typography>
      </Typography>
      <Typography variant="body2">Débloqué : <strong>{n.debloques}</strong></Typography>
      <Typography variant="body2">
        Terminé : <strong>{n.termines}</strong>{passage !== null && ` (${passage} % des débloqués)`}
      </Typography>
      <Typography variant="body2">Bloqués ou partis : <strong>{n.debloques - n.termines}</strong></Typography>
      <Typography variant="body2">
        Parties jouées : <strong>{n.parties}</strong> par {n.joueurs} joueur(s)
        {n.joueurs > 0 && ` · ${virgule(Math.round((n.parties / n.joueurs) * 10) / 10)} par joueur`}
      </Typography>
      <Typography variant="body2">Échecs : <strong>{n.echecs}</strong></Typography>
      <Typography variant="body2">
        Abandons en cours de partie : <strong>{n.abandons ?? 0}</strong>
        <Typography component="span" variant="caption" color="text.secondary"> (mesurés depuis la migration 242)</Typography>
      </Typography>
    </Box>
  );
}

const FENETRE_EVOLUTION = 30;

/** « 2026-09-27 » → « 27/09 ». */
const jourCourt = (iso) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

function InfoJour({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const j = payload[0].payload;
  return (
    <Box sx={{ bgcolor: 'background.paper', p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
        {new Date(`${label}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
      </Typography>
      <Typography variant="body2">Débloqué : <strong>{j.debloques}</strong></Typography>
      <Typography variant="body2">Terminé : <strong>{j.termines}</strong></Typography>
      <Typography variant="body2">Parties jouées : <strong>{j.parties}</strong></Typography>
      <Typography variant="body2">
        Échecs : <strong>{j.echecs}</strong>
        {j.parties > 0 && ` (${Math.round((j.echecs / j.parties) * 100)} % des parties)`}
      </Typography>
    </Box>
  );
}

/**
 * Les quatre variables de l'entonnoir pour UN niveau, jour par jour : pour voir
 * l'effet d'une retouche dans le temps. Mêmes comptes que la section (`jours`,
 * `segment`). Définitions : `admin.queries` → `getAventureNiveauJours`.
 */
function EvolutionNiveau({ niveau, jours, segment }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const niveauId = niveau?.niveauId;

  useEffect(() => {
    if (!niveauId) return undefined;
    let annule = false;
    setLoading(true);
    AdminService.getAdventureLevelDaily(niveauId, { days: jours, segment, fenetre: FENETRE_EVOLUTION })
      .then((d) => { if (!annule) { setData(d.jours); setError(null); } })
      .catch((err) => {
        console.error('Error fetching adventure level daily:', err);
        if (!annule) setError("Impossible de charger l'évolution du niveau");
      })
      .finally(() => { if (!annule) setLoading(false); });
    return () => { annule = true; };
  }, [niveauId, jours, segment]);

  if (!niveau) return null;
  const total = (cle) => (data ?? []).reduce((s, j) => s + j[cle], 0);
  const parties = total('parties');
  const echecs = total('echecs');

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
        Évolution sur {FENETRE_EVOLUTION} jours — {niveau.ordre}. {niveau.nom}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Chaque jour : joueurs qui ont débloqué ou terminé le niveau ce jour-là (débloqué = jour où le
        dernier prérequis a été terminé), parties jouées et échecs sur ce niveau.
        {data && ` Sur la période : ${total('debloques')} débloqué(s), ${total('termines')} terminé(s), ${parties} partie(s), ${echecs} échec(s)${parties > 0 ? ` — ${Math.round((echecs / parties) * 100)} % d'échecs` : ''}.`}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <Box sx={{ height: 320, width: '100%', opacity: loading ? 0.5 : 1 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data ?? []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="jour" tickFormatter={jourCourt} tick={{ fontSize: 11 }} minTickGap={16} />
              <YAxis yAxisId="joueurs" allowDecimals={false} />
              <YAxis yAxisId="parties" orientation="right" allowDecimals={false} />
              <RechartsTooltip content={<InfoJour />} />
              <Legend />
              <Line yAxisId="joueurs" type="monotone" dataKey="debloques" name="Débloqué" stroke="#90caf9" strokeWidth={2} dot={false} />
              <Line yAxisId="joueurs" type="monotone" dataKey="termines" name="Terminé" stroke="#2196f3" strokeWidth={2} dot={false} />
              <Line yAxisId="parties" type="monotone" dataKey="parties" name="Parties jouées" stroke="#ff9800" strokeWidth={2} dot={false} />
              <Line yAxisId="parties" type="monotone" dataKey="echecs" name="Échecs" stroke="#f44336" strokeWidth={2} strokeDasharray="5 5" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
}

function EntonnoirNiveaux({ niveaux, partiesMesureesDepuis, jours, segment }) {
  const biomes = [];
  niveaux.forEach((n) => {
    if (!biomes.find((b) => b.id === n.biomeId)) biomes.push({ id: n.biomeId, nom: n.biomeNom });
  });
  // Premier biome qui a des joueurs (la Forêt en pratique), sinon le premier tout court.
  const parDefaut = biomes.find((b) => niveaux.some((n) => n.biomeId === b.id && n.debloques > 0))?.id ?? biomes[0]?.id;
  const [biome, setBiome] = useState(null);
  const biomeActif = biome ?? parDefaut;
  const data = niveaux.filter((n) => n.biomeId === biomeActif);
  // Niveau suivi dans le temps : choisi dans la liste ou d'un clic sur sa barre ;
  // par défaut le premier du biome affiché.
  const [niveauChoisi, setNiveauChoisi] = useState(null);
  const niveau = data.find((n) => n.niveauId === niveauChoisi) ?? data[0] ?? null;

  return (
    <Card sx={{ mt: 3 }}>
      <CardHeader
        title="Entonnoir par niveau"
        subheader="Où l'aventure décroche : joueurs qui ont débloqué, puis terminé chaque niveau, et parties jouées"
        avatar={<MapIcon color="secondary" />}
      />
      <CardContent>
        <ToggleButtonGroup
          value={biomeActif}
          exclusive
          size="small"
          onChange={(e, v) => { if (v) { setBiome(v); setNiveauChoisi(null); } }}
          sx={{ mb: 2, flexWrap: 'wrap' }}
        >
          {biomes.map((b) => (
            <ToggleButton key={b.id} value={b.id}>{b.nom}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Alert severity="info" sx={{ mb: 2 }}>
          {partiesMesureesDepuis
            ? `Les parties jouées par niveau (échecs compris) ne sont comptées que depuis le ${new Date(`${partiesMesureesDepuis}T12:00:00`).toLocaleDateString('fr-FR')} : avant, une partie ne gardait pas son niveau.`
            : "Les parties jouées par niveau (échecs compris) ne sont pas encore mesurées : elles le seront dès le déploiement de la migration 192. Débloqués et terminés, eux, couvrent tout l'historique."}
        </Alert>
        <Box sx={{ height: 380, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={data}
              onClick={(e) => { const n = e?.activePayload?.[0]?.payload; if (n) setNiveauChoisi(n.niveauId); }}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="ordre" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="joueurs" allowDecimals={false} />
              <YAxis yAxisId="parties" orientation="right" allowDecimals={false} />
              <RechartsTooltip content={<InfoNiveau />} />
              <Legend />
              <Bar yAxisId="joueurs" dataKey="debloques" name="Débloqué" fill="#bbdefb" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="joueurs" dataKey="termines" name="Terminé" fill="#2196f3" radius={[4, 4, 0, 0]} />
              <Line yAxisId="parties" type="monotone" dataKey="parties" name="Parties jouées" stroke="#ff9800" strokeWidth={2} />
              <Line yAxisId="parties" type="monotone" dataKey="echecs" name="Échecs" stroke="#f44336" strokeWidth={2} strokeDasharray="5 5" />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
        <TextField
          select
          size="small"
          label="Niveau suivi dans le temps (ou cliquer une barre)"
          value={niveau?.niveauId ?? ''}
          onChange={(e) => setNiveauChoisi(e.target.value)}
          sx={{ mt: 3, minWidth: 320 }}
        >
          {data.map((n) => (
            <MenuItem key={n.niveauId} value={n.niveauId}>{n.ordre}. {n.nom} ({n.type})</MenuItem>
          ))}
        </TextField>
        <EvolutionNiveau niveau={niveau} jours={jours} segment={segment} />
      </CardContent>
    </Card>
  );
}

export default function PaliersAventureSection({ segment = 'familles' }) {
  const [jours, setJours] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let annule = false;
    setLoading(true);
    AdminService.getAdventureMilestones(jours, segment)
      .then((d) => { if (!annule) { setData(d); setError(null); } })
      .catch((err) => {
        console.error('Error fetching adventure milestones:', err);
        if (!annule) setError("Impossible de charger les paliers d'aventure");
      })
      .finally(() => { if (!annule) setLoading(false); });
    return () => { annule = true; };
  }, [jours, segment]);

  // La courbe s'arrête au dernier rang atteint : la traîne vide n'apprend rien.
  const courbe = data?.courbe ?? [];
  const dernierRang = courbe.reduce((max, r) => (r.joueurs > 0 ? r.rang : max), 0);
  const courbeUtile = courbe.filter((r) => r.rang <= Math.max(dernierRang, 16));

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          Progression dans l'aventure
        </Typography>
        <ToggleButtonGroup value={jours} exclusive size="small" onChange={(e, v) => { if (v !== null) setJours(v); }}>
          <ToggleButton value={30}>Inscrits 30 j</ToggleButton>
          <ToggleButton value={90}>90 j</ToggleButton>
          <ToggleButton value={365}>1 an</ToggleButton>
          <ToggleButton value={0}>Tous</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Niveau N = le N-ième niveau jouable terminé (prologues et épilogues exclus ; sur la Forêt,
        le 16ᵉ est le boss). Délai = de la création du compte à la première réussite du niveau.
        Temps de jeu = durée cumulée des sessions de jeu commencées avant la réussite du niveau
        (même reconstitution que la section « Durée des sessions ») : une estimation basse.
        {data && ` Base : ${data.comptes} comptes${jours ? ` créés depuis ${jours} jours` : ''}, dont ${data.aventuriers} ont terminé au moins un niveau.`}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {(data?.paliers ?? []).map((p) => (
              <Grid item xs={12} md={4} key={p.palier}>
                <PalierCard p={p} />
              </Grid>
            ))}
          </Grid>
          <Card>
            <CardHeader
              title="Joueurs par nombre de niveaux terminés"
              subheader="Combien de joueurs ont terminé au moins N niveaux jouables"
              avatar={<MapIcon color="primary" />}
            />
            <CardContent>
              <Box sx={{ height: 300, width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={courbeUtile}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="rang" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} />
                    <RechartsTooltip
                      labelFormatter={(r) => `${r} niveau(x) terminé(s)`}
                      formatter={(v) => [v, 'Joueurs']}
                    />
                    <Bar dataKey="joueurs" radius={[4, 4, 0, 0]}>
                      {courbeUtile.map((r) => (
                        <Cell key={r.rang} fill={COULEURS[r.rang] ?? '#90a4ae'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
          <EntonnoirNiveaux
            niveaux={data?.niveaux ?? []}
            partiesMesureesDepuis={data?.partiesMesureesDepuis}
            jours={jours}
            segment={segment}
          />
        </>
      )}
    </Box>
  );
}
